import { useMemo, useState } from "react";

import dayjs from "dayjs";

import { Search } from "@similarweb/icons";
import type {
    MonitorandprotectCampaignsListData,
    MonitorAndProtectModelCampaignsCampaignListItemModel,
} from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { SegmentedControl } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { useUserNames } from "app/pages/digital-marketing/monitor-and-protect/services/api/users";
import { useCountries } from "app/pages/digital-marketing/monitor-and-protect/services/country/use-countries";
import { LimitsBanner } from "app/pages/digital-marketing/monitor-and-protect/shared/components/limits/limits-banner";
import { ShareDialog } from "app/pages/digital-marketing/monitor-and-protect/shared/components/share-dialog";
import { useTranslation } from "pro-features/components/WithTranslation";

import { CampaignPermissions } from "../../../services/asset-permissions";

import { CampaignsList } from "./campaigns-list";

const sortCampaigns = (campaignList: MonitorandprotectCampaignsListData) =>
    [...campaignList].sort((a, b) => {
        if (a.type === "Demo" && b.type !== "Demo") {
            return 1;
        }
        if (a.type !== "Demo" && b.type === "Demo") {
            return -1;
        }
        return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    });

interface IWithCampaignsViewProps {
    campaigns: MonitorAndProtectModelCampaignsCampaignListItemModel[];
}

export const WithCampaignsView = ({ campaigns: data }: IWithCampaignsViewProps) => {
    const translate = useTranslation();

    const campaignTabs = useMemo(
        () => ({
            all: { index: 0, labelTemplate: translate("monitor_and_protect.homepage.campaigns.tab.all") },
            my: { index: 1, labelTemplate: translate("monitor_and_protect.homepage.campaigns.tab.my") },
            shared: { index: 2, labelTemplate: translate("monitor_and_protect.homepage.campaigns.tab.shared") },

            getSegmentedControlValues: (myCampaignsLength: number, sharedCampaignsLength: number) => [
                { label: `${campaignTabs.all.labelTemplate} (${myCampaignsLength + sharedCampaignsLength})` },
                { label: `${campaignTabs.my.labelTemplate} (${myCampaignsLength})` },
                { label: `${campaignTabs.shared.labelTemplate} (${sharedCampaignsLength})` },
            ],
        }),
        [translate],
    );

    const [selectedIndex, setSelectedIndex] = useState<number>(campaignTabs.all.index);
    const [searchQuery, setSearchQuery] = useState("");
    const { data: usersMap } = useUserNames();
    const { getCountryById } = useCountries();

    const [shareLink, setShareLink] = useState("");

    const {
        myCampaignsLength,
        sharedCampaignsLength,
        campaigns,
    }: {
        myCampaignsLength: number;
        sharedCampaignsLength: number;
        campaigns: MonitorandprotectCampaignsListData;
    } = useMemo(() => {
        const safeData = Array.isArray(data) ? data : [];

        const { myCampaigns, sharedCampaigns } = safeData.reduce(
            (acc, campaign) => {
                const campaignPermissions = new CampaignPermissions(campaign);
                if (campaign.type === "Demo" || !campaignPermissions.isOwner) {
                    acc.sharedCampaigns.push(campaign);
                } else {
                    acc.myCampaigns.push(campaign);
                }
                return acc;
            },
            { myCampaigns: [], sharedCampaigns: [] } as {
                myCampaigns: MonitorandprotectCampaignsListData;
                sharedCampaigns: MonitorandprotectCampaignsListData;
            },
        );

        const sortedMyCampaigns = sortCampaigns(myCampaigns);
        const sortedSharedCampaigns = sortCampaigns(sharedCampaigns);

        const selectedCampaigns = (): MonitorandprotectCampaignsListData => {
            switch (selectedIndex) {
                case campaignTabs.all.index:
                    return [...sortedMyCampaigns, ...sortedSharedCampaigns];
                case campaignTabs.my.index:
                    return sortedMyCampaigns;
                case campaignTabs.shared.index:
                    return sortedSharedCampaigns;
                default:
                    return [];
            }
        };

        const filteredCampaigns = selectedCampaigns().filter((campaign) => {
            if (!searchQuery.trim()) {
                return true;
            }
            const query = searchQuery.toLowerCase().trim();

            return campaign.name?.toLowerCase().includes(query);
        });

        return {
            myCampaignsLength: myCampaigns.length ?? 0,
            sharedCampaignsLength: sharedCampaigns.length ?? 0,
            campaigns: filteredCampaigns,
        };
    }, [campaignTabs, data, selectedIndex, searchQuery]);

    return (
        <>
            <ShareDialog
                isOpen={!!shareLink}
                onClose={() => setShareLink("")}
                shareLink={shareLink}
                contentText={translate("monitor_and_protect.homepage.share_dialog.content")}
            />
            <Flex
                paddingY="40px"
                width="100%"
                justifyContent="center"
            >
                <Stack
                    width="804px"
                    gap="24px"
                >
                    <LimitsBanner />

                    <Flex
                        width="100%"
                        gap="16px"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Flex
                            flexShrink={0}
                            height="40px"
                        >
                            <SegmentedControl
                                selectedIndex={selectedIndex}
                                onChange={setSelectedIndex}
                                values={campaignTabs.getSegmentedControlValues(
                                    myCampaignsLength,
                                    sharedCampaignsLength,
                                )}
                            />
                        </Flex>
                        <Flex
                            flex={1}
                            justifyContent="end"
                        >
                            <TextField
                                startAddon={
                                    <Search
                                        size="sm"
                                        color={colorsPalettes.carbon[200]}
                                    />
                                }
                                placeholder={translate("monitor_and_protect.homepage.campaigns.search.placeholder")}
                                value={searchQuery}
                                onChange={setSearchQuery}
                            />
                        </Flex>
                    </Flex>

                    <CampaignsList
                        campaigns={campaigns}
                        usersMap={usersMap}
                        onShare={setShareLink}
                        searchQuery={searchQuery}
                    />
                </Stack>
            </Flex>
        </>
    );
};
